import env from '@eneco/common/env';
import Logger from '@eneco/common/log';
import { getSession, signOut } from 'next-auth/react';

type SignOutCleanup = () => void | Promise<void>;

// Apps register local cleanup (cookies, cached selections, …) here so it runs
// on every full logout, including auto-logout from SessionExpiryHandler.
// Keeps @eneco/auth free of app imports (no circular dependency).
const cleanupHandlers = new Set<SignOutCleanup>();

export const registerSignOutCleanup = (handler: SignOutCleanup): (() => void) => {
  cleanupHandlers.add(handler);
  return () => {
    cleanupHandlers.delete(handler);
  };
};

/**
 * Runs any registered local cleanup, clears the next-auth session and redirects
 * to Okta's end-session endpoint so the IdP session is terminated too. Generic
 * (env-driven, no app state) so it can be reused wherever a full logout is needed.
 */
export const signOutWithOkta = async ({
  signOutUrl = env('OKTA_OAUTH2_LOGOUT'),
  redirectUri = env('OKTA_OAUTH2_LOGOUT_REDIRECT_URI'),
}: {
  signOutUrl?: string;
  redirectUri?: string;
} = {}): Promise<void> => {
  await Promise.all([...cleanupHandlers].map(handler => handler()));

  const session = await getSession();

  if (session) {
    const oktaSignoutUrl = `${signOutUrl}?id_token_hint=${session?.idToken}&post_logout_redirect_uri=${redirectUri}`;
    signOut({ redirect: false }).then(() => (window.location.href = oktaSignoutUrl));
    return;
  }

  Logger.error('4xignI', 'Tried logging out without session');
};
